import { useRef, useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { createContexts } from '@/js/libs/create-contexts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { useState$, useSubscribe } from '@/js/libs/observable';
import { ModalDialog } from '@/js/libs/ui/src/V2';

interface CommonConfirmationProps {
  message: string;
  title?: string;
  confirmButtonText?: string;
  isError?: boolean;
  /** Highlighted block under the message, for a caveat the user has to notice before confirming. */
  notification?: ComponentProps<typeof ModalDialog>['notification'];
  onActionClick?: never;
}
interface ConfirmationPropsWithOnConfirm<T> extends CommonConfirmationProps {
  onConfirm: () => Promise<T>;
}

type ConfirmationAskProps<T> = CommonConfirmationProps | ConfirmationPropsWithOnConfirm<T>;

export const { useConfirmationModalContext } = createContexts({
  ConfirmationModal: () => {
    const [modalProps, setModalProps] = useState<ConfirmationAskProps<unknown>>();
    const [pending$, setPending] = useState$<boolean>(false);

    const cancelRef = useRef<(() => void) | undefined>();
    const confirmRef = useRef<(() => Promise<void>) | undefined>();

    const modalState = useModalState({ onClose: cancelRef.current });

    const onActionClick = useCallbackRef(async () => {
      try {
        setPending(true);
        const onConfirm = confirmRef.current;
        if (onConfirm) await onConfirm();
      } finally {
        setPending(false);
        modalState.close();
      }
    });

    return {
      modalState,
      pending$,
      modalProps,
      onActionClick,
      open: ({
        props,
        onConfirm,
        onCancel,
      }: {
        props: ConfirmationAskProps<unknown>;
        onConfirm: () => Promise<void>;
        onCancel: () => void;
      }) => {
        setModalProps(props);
        confirmRef.current = onConfirm;
        cancelRef.current = onCancel;
        modalState.open();
      },
    };
  },
});

const Modal = () => {
  const [t] = useTranslation(['backend', 'common', 'messages']);
  const { modalState, modalProps, pending$, onActionClick } = useConfirmationModalContext();
  const { message, title, confirmButtonText, isError, notification } = modalProps ?? {};

  const pending = useSubscribe(pending$);

  return (
    modalState.isMounted && (
      <ModalDialog
        state={modalState}
        title={title ?? t('common:CONFIRM')}
        text={message}
        notification={notification}
        onActionButtonClick={onActionClick}
        cancelButtonText={t('common:CANCEL')}
        actionButtonText={confirmButtonText ?? t('common:YES_LABEL')}
        isPendingAction={pending}
        error={isError ?? false}
      />
    )
  );
};

/**
 * Allow ask user confirmation without bloat
 * .ask return Promise - it will be resolved after modal was closed. If it resolved to `false` - modal was closed without confirmation.
 * * Note: modal closed only AFTER promise that return onConfirm will be resolved or rejected
 * * if onConfirm throws error, promise will be rejected with this error
 * @example
 * const confirm = useConfirmation();
 * // Simple
 * await confirm.ask({ message: 'Are you sure?' });
 *
 * // Regular
 * await confirm.ask({
 *   message: 'Are you sure?',
 *   onConfirm: async () => {
 *     await doSomthing();
 *   },
 * });
 *
 * // Advanced
 * const wasConfirmed = await confirm.ask({
 *   message: 'Are you sure?',
 *   title: 'Confirmation',
 *   confirmButtonText: 'Confirm',
 *   isError: true,
 *   onConfirm: async () => {
 *     await doSomthingAfter();
 *     return true;
 *   },
 * });
 * // * Note that in case user canceled confirmation, promise will be resolved with `false`
 */
type AskReturn<P> = P extends { onConfirm: () => Promise<infer T> } ? Promise<T | false> : Promise<boolean>;

type AskCb = {
  <T extends ConfirmationAskProps<unknown>>(props: T): AskReturn<T>;
};

export function useConfirmation() {
  const modalCtx = useConfirmationModalContext();

  const ask = useCallbackRef(
    ((props) =>
      new Promise<unknown | false>((resolve, reject) => {
        modalCtx.open({
          props,
          onConfirm: async () => {
            if ('onConfirm' in props) {
              try {
                const result = await props.onConfirm();
                resolve(result);
              } catch (error) {
                reject(error);
              }
            } else {
              resolve(true);
            }
          },
          onCancel: () => resolve(false),
        });
      })) as AskCb,
  );

  return { ask };
}

useConfirmation.Modal = Modal;
